import { useEffect, useRef } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = "website",
  jsonLd,
}: SEOHeadProps) {
  const jsonLdIdRef = useRef<string | null>(null);

  useEffect(() => {
    const siteUrl =
      (import.meta as Record<string, unknown>).env &&
      ((import.meta as Record<string, unknown>).env as Record<string, string>).VITE_SITE_URL
        ? ((import.meta as Record<string, unknown>).env as Record<string, string>).VITE_SITE_URL
        : "https://insuranceguardian.readdy.ai";
    const fullUrl = `${String(siteUrl).replace(/\/$/, "")}${canonicalPath}`;

    document.title = title;

    const setMeta = (attrName: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(
        `meta[${attr}="${attrName}"]`
      ) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrName);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", fullUrl, true);
    setMeta("og:type", ogType, true);
    if (ogImage) setMeta("og:image", ogImage, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", fullUrl);

    if (jsonLd) {
      if (jsonLdIdRef.current) {
        const oldEl = document.getElementById(jsonLdIdRef.current);
        if (oldEl) oldEl.remove();
      }

      const id = `seo-jsonld-${Date.now()}`;
      jsonLdIdRef.current = id;

      const scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.id = id;
      const ldString = JSON.stringify(jsonLd).replace(
        /__SITE_URL__/g,
        String(siteUrl).replace(/\/$/, "")
      );
      scriptEl.textContent = ldString;
      document.head.appendChild(scriptEl);
    }

    return () => {
      if (jsonLdIdRef.current) {
        const el = document.getElementById(jsonLdIdRef.current);
        if (el) el.remove();
      }
    };
  }, [title, description, keywords, canonicalPath, ogImage, ogType, jsonLd]);

  return null;
}